import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { apiJson, buildApiUrl } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('srh_token'));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!token);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setUser(null);
      setLoading(false);
      return () => {};
    }

    setLoading(true);
    setError(null);

    fetch(buildApiUrl('/api/auth/me'), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load session');
        const data = await res.json();
        if (!cancelled) setUser(data.user);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Session check failed:', err);
          setUser(null);
          setToken(null);
          localStorage.removeItem('srh_token');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = async (email, password) => {
    const data = await apiJson('/api/auth/login', { method: 'POST', body: { email, password } });
    setToken(data.token);
    localStorage.setItem('srh_token', data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const data = await apiJson('/api/auth/register', { method: 'POST', body: { name, email, password } });
    setToken(data.token);
    localStorage.setItem('srh_token', data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('srh_token');
  };

  const value = useMemo(
    () => ({ token, user, loading, error, login, register, logout }),
    [token, user, loading, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
